import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/components/AuthProvider";
import { BillingProvider } from "@/components/BillingProvider";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import WorkspaceView from "@/pages/WorkspaceView";
import BoardView from "@/pages/BoardView";
import Settings from "@/pages/Settings";
import NotFound from "@/pages/NotFound";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import Help from "@/pages/Help";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import BackgroundBlobs from "@/components/BackgroundBlobs";
import ShareAccept from "@/pages/ShareAccept";
import AdminRoute from "@/components/AdminRoute";
import AdminShell from "@/components/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminLegal from "@/pages/admin/AdminLegal";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminPromotions from "@/pages/admin/AdminPromotions";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminSupportTicket from "@/pages/admin/AdminSupportTicket";
import AdminOrganizations from "@/pages/admin/AdminOrganizations";
import Terms from "@/pages/Terms";
import ResetPassword from "@/pages/ResetPassword";
import Support from "@/pages/Support";
import SupportTicket from "@/pages/SupportTicket";
import OrgAdmin from "@/pages/OrgAdmin";
import TeamChat from "@/pages/TeamChat";
import ForTeams from "@/pages/ForTeams";
import Calendar from "@/pages/Calendar";
import { TenantProvider } from "@/components/TenantProvider";
import TenantRedirect from "@/components/TenantRedirect";
import FreeLimitDialog from "@/components/FreeLimitDialog";

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/for-teams" element={<ForTeams />} />
      <Route path="/t/:tenantSlug/*" element={<TenantRedirect />} />
      <Route path="/share/:token" element={<ShareAccept />} />
      <Route path="/pricing" element={
        <ProtectedRoute><Pricing /></ProtectedRoute>
      } />
      <Route path="/billing/success" element={
        <ProtectedRoute><BillingSuccess /></ProtectedRoute>
      } />
      <Route
        element={
          <AdminRoute>
            <AdminShell />
          </AdminRoute>
        }
      >
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
        <Route path="/admin/payments" element={<AdminPayments />} />
        <Route path="/admin/legal" element={<AdminLegal />} />
        <Route path="/admin/plans" element={<AdminPlans />} />
        <Route path="/admin/promotions" element={<AdminPromotions />} />
        <Route path="/admin/support" element={<AdminSupport />} />
        <Route path="/admin/support/:ticketId" element={<AdminSupportTicket />} />
        <Route path="/admin/organizations" element={<AdminOrganizations />} />
      </Route>
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/workspaces/:workspaceId" element={<WorkspaceView />} />
        <Route path="/boards/:boardId" element={<BoardView />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/help" element={<Help />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/support" element={<Support />} />
        <Route path="/support/:ticketId" element={<SupportTicket />} />
        <Route path="/org" element={<OrgAdmin />} />
        <Route path="/chat" element={<TeamChat />} />
      </Route>
      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App relative">
      <BackgroundBlobs />
      <BrowserRouter>
        <TenantProvider>
          <AuthProvider>
            <BillingProvider>
              <AppRouter />
              <FreeLimitDialog />
            </BillingProvider>
          </AuthProvider>
        </TenantProvider>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
